import React, { useState } from 'react';
import { MapPin, ChevronDown, MessageCircle, User } from 'react-feather';
import {
  InlineShell,
  InlineEyebrow,
  CardButton,
  CardAvatar,
  hashHue,
  type F8CardData,
  type OnAction,
} from './cardHelpers';
import { normalizeInterests } from '../../../utils/interests';

interface Person {
  uid?: string;
  userId?: string;
  tetherHandle?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  city?: string;
  country?: string;
  aboutMe?: string;
  photoURL?: string;
  avatar?: string;
  isOnline?: boolean;
  interests?: string[];
}

function PersonRow({ person, onAction }: { person: Person; onAction?: OnAction }) {
  const [expanded, setExpanded] = useState(false);

  const id = person.uid || person.userId;
  const tetherHandle = person.tetherHandle || '';
  const handleDisplay = tetherHandle ? `@${tetherHandle}` : '';
  const photo = person.photoURL || person.avatar;
  const firstName = person.firstName || person.name || '';
  const initial = (tetherHandle || firstName || '?')[0].toUpperCase();
  const hue = hashHue(tetherHandle || firstName);
  const location = [person.city, person.country].filter(Boolean).join(', ');
  const bio = person.aboutMe;
  const hasBio = !!(bio && bio.trim());

  const thumbStyle: React.CSSProperties = {
    background: `linear-gradient(135deg,
      oklch(0.50 0.22 ${hue}) 0%,
      oklch(0.55 0.24 ${(hue + 30) % 360}) 50%,
      oklch(0.65 0.26 ${(hue + 60) % 360}) 100%)`,
  };

  return (
    <div className="f8c-person-row">
      <div
        className="f8c-person-head"
        onClick={() => (hasBio ? setExpanded((e) => !e) : undefined)}
        style={hasBio ? { cursor: 'pointer' } : undefined}
      >
        {photo ? (
          <CardAvatar src={photo} name={handleDisplay || firstName} size={56} className="f8c-person-avatar" />
        ) : (
          <div className="f8c-person-thumb" style={thumbStyle}>
            <span className="f8c-person-thumb-initial">{initial}</span>
          </div>
        )}

        <div className="f8c-person-identity">
          <div className="f8c-person-name">
            {handleDisplay || firstName || 'Unknown'}
            {person.isOnline && <span className="f8c-person-online" aria-label="online" />}
          </div>
          {location && (
            <div className="f8c-person-meta">
              <MapPin size={11} />
              <span>{location}</span>
            </div>
          )}
        </div>

        {hasBio && (
          <ChevronDown
            size={14}
            className="f8c-person-chev"
            style={{ transform: expanded ? 'rotate(180deg)' : 'none' }}
          />
        )}
      </div>

      {hasBio && expanded && (
        <div className="f8c-person-detail">
          <p className="f8c-person-bio">{bio}</p>
          {normalizeInterests(person.interests).length > 0 && (
            <div className="f8c-person-interests">
              {normalizeInterests(person.interests).map((tag) => (
                <span key={tag} className="f8c-profile-interest">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="f8c-person-actions">
        <CardButton
          primary
          small
          onClick={() =>
            onAction?.({
              action: 'navigate',
              params: {
                screen: 'UserChatScreen',
                recipientId: id,
                recipientName: handleDisplay || firstName,
              },
            })
          }
        >
          <MessageCircle size={12} />
          Message
        </CardButton>
        <CardButton
          small
          onClick={() =>
            onAction?.({
              action: 'navigate',
              params: { screen: 'PublicUserProfile', userId: id },
            })
          }
        >
          <User size={12} />
          Profile
        </CardButton>
      </div>
    </div>
  );
}

export default function PeopleListCard({ card, onAction }: { card: F8CardData; onAction?: OnAction }) {
  const people: Person[] = card?.data?.people || [];
  if (people.length === 0) return null;

  return (
    <InlineShell>
      <InlineEyebrow label={`${people.length} people`} />
      <div className="f8c-person-list">
        {people.map((person, i) => (
          <PersonRow key={person.uid || person.userId || i} person={person} onAction={onAction} />
        ))}
      </div>
    </InlineShell>
  );
}
